import type { QueueClaimReleaseReason, UnderwritingQueueClaimHistory } from "@/models/underwriting";

export type QueueHistorySort = "date" | "application";
export type QueueHistoryDirection = "latest" | "oldest";
export type QueueHistoryEventType = "Claimed" | QueueClaimReleaseReason;

export interface QueueHistoryFilters {
  agentId?: number;
  eventType?: QueueHistoryEventType;
  start?: string;
  end?: string;
}

export const QUEUE_HISTORY_EVENT_LABELS: Record<QueueHistoryEventType, string> = {
  Claimed: "Claimed",
  Actioned: "Actioned",
  TakenOver: "Taken over",
  Abandoned: "Abandoned",
  Unclaimed: "Unclaimed",
};

export interface QueueHistoryRow {
  id: string;
  historyId: number;
  applicationId: number;
  claimedBy: string;
  occurredOn: string;
  timestamp: number;
  eventType: QueueHistoryEventType;
  shaded: boolean;
}

export function getQueueHistoryRows(
  history: UnderwritingQueueClaimHistory[],
  sort: QueueHistorySort,
  applicationSearch = "",
  direction: QueueHistoryDirection = "latest",
  filters: QueueHistoryFilters = {}
): QueueHistoryRow[] {
  const rows: QueueHistoryRow[] = [];
  const search = applicationSearch.trim();
  const selectedStart = filters.start ? Date.parse(filters.start) : -Infinity;
  const selectedEnd = filters.end ? Date.parse(filters.end) : Infinity;

  for (const claim of history) {
    if (filters.agentId !== undefined && claim.agentId !== filters.agentId) continue;
    if (!String(claim.applicationId).includes(search)) continue;
    const periodStart = Date.parse(claim.periodStart);
    const periodEnd = Date.parse(claim.periodEnd);
    const events: { occurredOn: string | null; eventType: QueueHistoryEventType }[] = [
      { occurredOn: claim.claimedOn, eventType: "Claimed" },
    ];
    if (claim.releaseReason) {
      events.push({ occurredOn: claim.releasedOn, eventType: claim.releaseReason });
    }

    for (const event of events) {
      if (filters.eventType && event.eventType !== filters.eventType) continue;
      if (!event.occurredOn) continue;
      const timestamp = Date.parse(event.occurredOn);
      if (!Number.isFinite(timestamp) || timestamp < periodStart || timestamp > periodEnd
        || timestamp < selectedStart || timestamp > selectedEnd) continue;
      rows.push({
        id: `${claim.underwritingQueueClaimHistoryId}-${event.eventType}`,
        historyId: claim.underwritingQueueClaimHistoryId,
        applicationId: claim.applicationId,
        claimedBy: claim.claimedBy,
        occurredOn: event.occurredOn,
        timestamp,
        eventType: event.eventType,
        shaded: false,
      });
    }
  }

  const latestByApplication = new Map<number, number>();
  for (const row of rows) {
    latestByApplication.set(row.applicationId, Math.max(
      latestByApplication.get(row.applicationId) ?? -Infinity,
      row.timestamp
    ));
  }

  const ordering = direction === "oldest" ? -1 : 1;
  rows.sort((first, second) => {
    if (sort === "application" && first.applicationId !== second.applicationId) {
      return ordering * ((latestByApplication.get(second.applicationId)! - latestByApplication.get(first.applicationId)!)
        || second.applicationId - first.applicationId);
    }
    return ordering * (second.timestamp - first.timestamp
      || second.historyId - first.historyId
      || Number(first.eventType === "Claimed") - Number(second.eventType === "Claimed"));
  });

  let groupIndex = -1;
  let previousApplicationId: number | undefined;
  return rows.map((row, rowIndex) => {
    if (row.applicationId !== previousApplicationId) groupIndex += 1;
    previousApplicationId = row.applicationId;
    return { ...row, shaded: (sort === "application" ? groupIndex : rowIndex) % 2 === 1 };
  });
}